import { useState, useCallback } from 'react';
import API_ROUTES from './apiRoutes';
import { useFetch } from './useFetch';
import { BannerModel } from '../Models/BannerModel';

export const useBanners = () => {
  const [banners, setBanners] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const { fetchWithAuth } = useFetch();

  const getToken = () => localStorage.getItem('token');

  const resetStates = useCallback(() => {
    setLoading(false);
    setError(null);
    setSuccess(false);
  }, []);

  const fetchBanners = useCallback(async () => {
    resetStates();
    setLoading(true);
    try {
      const response = await fetchWithAuth(API_ROUTES.BANNERS.default(), {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      const data = await response.json();
      const bannersList = data.banners || data;
      setBanners(BannerModel.fromJsonList(bannersList));
      setSuccess(true);
    } catch (err) {
      setError('Erro ao carregar banners');
    } finally {
      setLoading(false);
    }
  }, [resetStates, fetchWithAuth]);

  const createBanner = useCallback(async (bannerData) => {
    resetStates();
    setLoading(true);
    try {
      const response = await fetchWithAuth(API_ROUTES.BANNERS.default(), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${getToken()}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(bannerData)
      });
      if (!response.ok) throw new Error('Erro ao criar banner');
      await fetchBanners();
      setSuccess(true);
    } catch (err) {
      setError('Erro ao criar banner');
    } finally {
      setLoading(false);
    }
  }, [resetStates, fetchBanners, fetchWithAuth]);

  const updateBanner = useCallback(async (bannerId, bannerData) => {
    resetStates();
    setLoading(true);
    try {
      const response = await fetchWithAuth(API_ROUTES.BANNERS.update(bannerId), {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${getToken()}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(bannerData)
      });
      if (!response.ok) throw new Error('Erro ao atualizar banner');
      await fetchBanners();
      setSuccess(true);
    } catch (err) {
      setError('Erro ao atualizar banner');
    } finally {
      setLoading(false);
    }
  }, [resetStates, fetchBanners, fetchWithAuth]);

  const deleteBanner = useCallback(async (id) => {
    resetStates();
    setLoading(true);
    try {
      const response = await fetchWithAuth(API_ROUTES.BANNERS.delete(id), {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      if (!response.ok) throw new Error('Erro ao deletar banner');
      await fetchBanners();
      setSuccess(true);
    } catch (err) {
      setError('Erro ao deletar banner');
    } finally {
      setLoading(false);
    }
  }, [resetStates, fetchBanners, fetchWithAuth]);

  return { banners, loading, error, success, fetchBanners, createBanner, updateBanner, deleteBanner };
};
